/**
 * Opening a shared link: settle the city, land on the screen, say what moved.
 *
 * The other half of `src/share.js`. That file is pure and knows nothing about
 * this app; this one knows the network bundle, the store and the router, and
 * is the only place a link is allowed to change what is on screen.
 *
 * ── THE TWO WAYS A LINK ARRIVES ──────────────────────────────────────────────
 *
 * COLD START. The web app is opened at `/s/<token>`. The token is read HERE,
 * at module load, straight off `location` — before `main.js` pushes the first
 * route and before the router's onboarding guard runs. Reading it later would
 * mean reading it after `App.vue` has mounted, and `bus.emit('route')` in
 * main.js fires before mount, so a listener would simply miss it.
 *
 * ALREADY RUNNING. A native deep link arrives as `appUrlOpen`, which main.js
 * forwards to `app.route(url)` and the bus carries to `App.vue`. That calls
 * `offerShare(url)` and then `applyShare(router)`, which is the same path the
 * cold start takes one tick later.
 *
 * ── AND THE THINGS IT MUST NOT FIGHT ─────────────────────────────────────────
 *
 * ONBOARDING WINS. A first run has a four-step question in front of it and a
 * link that yanked somebody out of it mid-answer would lose the answer. The
 * descriptor is held instead; `App.vue` applies it when the welcome is done.
 *
 * THE URL IS TIDIED, once, as soon as the token is taken. `/s/<token>` becomes
 * `/app` in the address bar, so a refresh an hour later opens the recipient's
 * own board rather than re-applying a board they have finished with. The app
 * navigates through a memory history, so the address bar is decoration from
 * that moment on and there is nothing to keep in sync.
 *
 * ── DEGRADING HONESTLY ───────────────────────────────────────────────────────
 *
 * TIMES MOVE ON. Every link is a photograph of a moment, opened in a different
 * one, and the thing it names may be gone: the train has run, the disruption
 * has been cleared, the station is not in the city the recipient's app has
 * data for. NOT ONE OF THOSE IS AN ERROR SCREEN. Each degrades to the nearest
 * useful thing and `shareNote` carries one dry sentence saying what happened,
 * which the landing screen renders under its masthead. Per kind:
 *
 *   board    station unknown here → the recipient's own board, and say so.
 *            destination unknown → the plain board, and say so.
 *   line     route not in this city → the lines list, and say so.
 *   train    scheduled more than an hour ago, or no longer findable → the
 *            board at the station it was to be boarded from, and say that the
 *            train has run.
 *   trip     the board at FROM with TO as its destination, which plans the
 *            change from now — the screen that answers a trip since the Trip
 *            tab was retired. An end not in the bundle degrades like a board
 *            link's: no FROM is the recipient's own board, no TO is the plain
 *            board at FROM, and either says so.
 *   options  re-planned from now, because "another way around" is a
 *            question about the next hour and never about the hour it was
 *            asked in. An end not in the bundle lands on the board instead.
 *   alert    not in this phone's feed any more → the alerts ledger, saying the
 *            disruption has been cleared.
 *   map      the station is not in this city's bundle → the network map with
 *            nothing selected, and say so. The map itself never degrades:
 *            every city has one.
 */

import { ref } from 'vue';
import { unpackTime } from './share.js';
import { offerShare, takePendingShare } from './share-link.js';
import { onAppUrlOpen } from './core/native.js';
import { city, switchCity, lines, disruptions, stations, feedSettled } from './app-state.js';
import { stationById } from './network.js';
import { requestBoard } from './board-request.js';
import { requestMapStation } from './map-request.js';
import { requestOptions } from './options-request.js';
import { setTrip, place } from './directions-state.js';

/**
 * How long after its scheduled time a train is still worth opening.
 *
 * An hour. PTV keeps a run answerable for a while after it has gone and the
 * screen is still true while it does — the train ran, here is where it got to.
 * Past that the honest answer is the board, because a run reference with
 * nothing behind it renders a page of blanks.
 */
const TRAIN_GONE_MS = 60 * 60_000;


/**
 * One sentence about what was NOT restored, for the landing screen to show.
 *
 * A ref rather than a toast: a toast is gone in four seconds and this is the
 * explanation for why the screen is not the screen somebody was sent. The
 * screen clears it when the person moves on.
 */
export const shareNote = ref(null);

/**
 * A link is still being settled: the city is switching, or the feed that
 * decides where it lands has not answered yet.
 *
 * `App.vue` holds the boot screen up while this is true. The alternative is
 * the app painting the departures board for a second and then jumping to
 * somewhere else, which reads as a misfire rather than as a link opening —
 * and on the one path that actually waits, a fresh phone opening an alert
 * link, the splash is up anyway and this only stops it leaving early.
 */
export const shareSettling = ref(false);

/** The screen has read the note and the person has moved on. */
export function clearShareNote() {
  shareNote.value = null;
}

/** Tests only. */
export function clearPendingShare() {
  takePendingShare();
  shareNote.value = null;
}

export { offerShare, sharePending, peekPendingShare } from './share-link.js';

// ── The native cold start ────────────────────────────────────────────────────
//
// THE WEB COLD START IS READ OFF `location`; THE NATIVE ONE CANNOT BE. Inside
// the app `location` is the bundle, never the shared link, and the URL turns up
// afterwards as Capacitor's `appUrlOpen`. main.js forwards that to the bus —
// but the bus is subscribed to in `App.vue`'s `onMounted`, and the launch URL
// is delivered before the component mounts, so on a cold start `bus.emit` fires
// into an empty room and the link is lost without a trace. What that looks like
// from the outside is the app opening on the recipient's OWN board with the
// sender's destination quietly missing, which reads as the link having worked.
//
// So it is offered here, from module scope — the same pending slot the web cold
// start fills, filled while main.js is still importing and long before anything
// mounts. `App.vue`'s watch applies it on its first tick, exactly as it does for
// a link that came in off the address bar; its bus listener still covers a link
// arriving at an app already running; and `takePendingShare` makes whichever of
// them gets there second a no-op.
onAppUrlOpen((url) => { offerShare(url); });

// ── Applying one ─────────────────────────────────────────────────────────────

const stop = (id) => (Number(id) > 0 ? stationById(Number(id)) : null);

function note(text) {
  shareNote.value = text;
}

/**
 * Apply the waiting link, if there is one. Resolves when the screen is set.
 *
 * Taken before anything is awaited, so a second caller a tick later — the bus
 * and the cold start can both land on the same token — does the work once.
 */
export async function applyShare(router) {
  const desc = takePendingShare();
  if (!desc) return false;
  shareNote.value = null;
  shareSettling.value = true;

  try {
    // THE CITY FIRST, and awaited. Everything after this reads the network
    // bundle, and a Sydney stop id resolved against Melbourne's is either
    // nothing at all or, worse, somebody else's suburb.
    if (desc.city !== city.value) await switchCity(desc.city);

    const go = OPENERS[desc.page];
    if (!go) return false;
    const path = await go(desc);
    if (!path) return false;
    await router.replace(path).catch(() => { /* already there */ });
    return true;
  } catch (e) {
    // A link must never be able to stop the app booting. It opens where it
    // would have opened anyway and the person is none the wiser.
    console.warn('share: could not open link', e);
    return false;
  } finally {
    shareSettling.value = false;
  }
}

/** The board at a station, with the destination the sender had set. */
function openBoard(desc) {
  const station = stop(desc.stop);
  if (!station) {
    // "This is your usual board" only where there IS one. A recipient who has
    // never opened the app before gets the board's empty state, and a sentence
    // calling that their usual board would be the app telling them about a
    // habit they do not have.
    note(stations.value.length
      ? 'That station is not on the map here. This is your usual board.'
      : 'That station is not on the map here.');
    return '/tabs/departures';
  }
  const dest = desc.dest ? stop(desc.dest) : null;
  if (desc.dest && !dest) note('The destination in that link is not a station here.');
  requestBoard(station, { destStopId: dest?.stopId ?? null });
  return '/tabs/departures';
}

const OPENERS = {
  board: openBoard,

  map(desc) {
    // A MAP LINK ALWAYS LANDS ON THE MAP. Every other kind has somewhere
    // better to go when the thing it names has gone; this one does not, and it
    // does not need one — the network is the point and it is always there. So
    // the only degradation is the station, and it is said rather than
    // swallowed: a recipient in Sydney sent Richmond gets Sydney's network and
    // a sentence about why nothing is selected.
    if (desc.stop) {
      const station = stop(desc.stop);
      if (station) requestMapStation(station);
      else note('That station is not on the map here. This is the network.');
    }
    return '/tabs/map';
  },

  line(desc) {
    // `lineFor` never returns undefined into a template — it answers with an
    // "Unknown line" placeholder — so the question here has to be asked of the
    // list itself. A link to a line this city does not run would otherwise
    // open a grey page named after nothing.
    if (!lines.value.some((l) => Number(l.routeId) === Number(desc.route))) {
      note('That line is not one this city runs.');
      return '/tabs/home';
    }
    return `/tabs/line/${desc.route}`;
  },

  train(desc) {
    const at = unpackTime(desc.at);
    const station = stop(desc.stop);
    if (at && Date.now() - at > TRAIN_GONE_MS) {
      // The one degradation worth naming out loud. Somebody was sent a train
      // and the train has gone; the useful thing left is where it went from.
      note('That train has run. This is the board it left from.');
      if (station) requestBoard(station);
      return '/tabs/departures';
    }
    if (!station) {
      note('That station is not on the map here.');
      return '/tabs/departures';
    }
    return `/tabs/train/${encodeURIComponent(desc.run)}/${station.stopId}`;
  },

  trip(desc) {
    // THE BOARD, WITH THE DESTINATION SET. There is no Trip tab any more: the
    // departures board at FROM with TO on its chip plans the change itself,
    // from the recipient's own now — never from the moment it was shared,
    // because the options that link saw were about an earlier hour. Links
    // sent before the tab went away land here too, which is the point of
    // keeping the kind.
    const from = stop(desc.from);
    const to = stop(desc.to);
    if (!from && !to) {
      note('Neither end of that trip is a station here.');
      return '/tabs/departures';
    }
    if (!from) {
      note('One end of that trip is not a station here. This is your usual board.');
      return '/tabs/departures';
    }
    if (!to) note('One end of that trip is not a station here.');
    // The stash too, so a journey page or anything else that reads the two
    // ends agrees with the board about what the trip is. No options: the
    // board plans them.
    setTrip({ from: place(from), to: place(to), options: [], plannedAt: null, error: null });
    requestBoard(from, { destStopId: to?.stopId ?? null });
    return '/tabs/departures';
  },

  options(desc) {
    const from = stop(desc.from);
    const to = stop(desc.to);
    if (!from || !to) {
      note('One end of that link is not a station here.');
      if (from) requestBoard(from, { destStopId: to?.stopId ?? null });
      return '/tabs/departures';
    }
    // EXPLICIT STATIONS, both ends, and no place kind anywhere near it. See
    // the privacy note at the top of share.js: a link that said "home" would
    // say where the sender lives.
    setTrip({ from: place(from), to: place(to), options: [], plannedAt: null, error: null });
    requestOptions({ fromStopId: from.stopId, toStopId: to.stopId });
    return '/tabs/options';
  },

  async alert(desc) {
    const find = () => disruptions.value
      .find((d) => Number(d.ptvId) === Number(desc.alert) && !d.clearedAt);

    // AN EMPTY LEDGER IS NOT EVIDENCE THAT A DISRUPTION IS OVER. A recipient
    // opening their first ever link has no rows at all yet — the feed is
    // fetched after the app boots — so asking the ledger at this moment
    // answered "no longer running" about a notice that was running, every
    // time, on exactly the phones a link is most likely to land on. So where
    // the row is not already here, wait for the first feed (bounded; see
    // FEED_WAIT_MS) and ask again. `shareSettling` keeps the splash up while
    // this happens, rather than a tab of nothing.
    let row = find();
    if (!row) {
      await feedSettled();
      row = find();
    }
    if (!row) {
      // Now it means something: PTV has been heard from and this is not in
      // what it said, or nothing could be reached at all. Either way the
      // honest answer is the ledger and the sentence.
      note('That disruption is no longer running.');
      return '/tabs/alerts';
    }
    return `/tabs/disruption/${row.id}`;
  },
};
